/**
 * Modelo rítmico do curso.
 *
 * A unidade interna é o pulso da semínima (quarter note) = 1.
 * Assim, semínima = 1, mínima = 2, semibreve = 4, colcheia = 0,5 e
 * semicolcheia = 0,25 — exatamente como o aluno aprende na ordem das aulas.
 */

export type DurationId =
  | 'whole'
  | 'half'
  | 'quarter'
  | 'eighth'
  | 'sixteenth'
  | 'dotted-half'
  | 'dotted-quarter'
  | 'dotted-eighth';

export interface DurationDef {
  id: DurationId;
  /** duração em pulsos de semínima */
  beats: number;
  /** nome em português mostrado ao aluno */
  ptName: string;
  /** cabeça preenchida (cheia) ou vazia */
  filled: boolean;
  /** tem haste? */
  stem: boolean;
  /** número de bandeirolas (0, 1 ou 2) */
  flags: number;
  /** pontos de aumento à direita da figura (0 ou 1) */
  dots?: number;
}

export const DURATIONS: Record<DurationId, DurationDef> = {
  whole: { id: 'whole', beats: 4, ptName: 'Semibreve', filled: false, stem: false, flags: 0 },
  half: { id: 'half', beats: 2, ptName: 'Mínima', filled: false, stem: true, flags: 0 },
  quarter: { id: 'quarter', beats: 1, ptName: 'Semínima', filled: true, stem: true, flags: 0 },
  eighth: { id: 'eighth', beats: 0.5, ptName: 'Colcheia', filled: true, stem: true, flags: 1 },
  sixteenth: {
    id: 'sixteenth',
    beats: 0.25,
    ptName: 'Semicolcheia',
    filled: true,
    stem: true,
    flags: 2,
  },
  'dotted-half': {
    id: 'dotted-half',
    beats: 3,
    ptName: 'Mínima pontuada',
    filled: false,
    stem: true,
    flags: 0,
    dots: 1,
  },
  'dotted-quarter': {
    id: 'dotted-quarter',
    beats: 1.5,
    ptName: 'Semínima pontuada',
    filled: true,
    stem: true,
    flags: 0,
    dots: 1,
  },
  'dotted-eighth': {
    id: 'dotted-eighth',
    beats: 0.75,
    ptName: 'Colcheia pontuada',
    filled: true,
    stem: true,
    flags: 1,
    dots: 1,
  },
};

export const DURATION_IDS: DurationId[] = [
  'whole',
  'half',
  'quarter',
  'eighth',
  'sixteenth',
  'dotted-half',
  'dotted-quarter',
  'dotted-eighth',
];

export function durationBeats(id: DurationId): number {
  return DURATIONS[id].beats;
}

/** Quantos "quartos de pulso" a figura vale (usado na geração de compassos). */
export function durationUnits(id: DurationId): number {
  return Math.round(DURATIONS[id].beats * 4);
}

export function durationName(id: DurationId): string {
  return DURATIONS[id].ptName;
}

/** Descrição curta da duração em pulsos, para textos de apoio. */
export function durationBeatLabel(id: DurationId): string {
  const { beats } = DURATIONS[id];
  if (beats === 1) return '1 pulso';
  if (beats === 1.5) return 'um pulso e meio';
  if (beats > 1) return `${beats} pulsos`;
  if (beats === 0.5) return 'meio pulso';
  if (beats === 0.75) return '¾ de pulso';
  return 'um quarto de pulso';
}